// Sample Page Build job for the task-type gallery (/agents/task-types/page_build). Not a real job: it shows what a
// Templated Content Fix looks like as a Page Build at each stage, on a made-up rewrite of a real page
// (BDT Electric's Langley emergency page, 80% the same as its Hope page in the templated-content screen).
// No database, no writes.

import type { AiJob } from '../../../../lib/supabase.types';
import type { OperatorSummary } from '../../../../lib/aiJobSummary';

export const PAGE_BUILD_CLIENT_NAME = 'BDT Electric';
export type GalleryStage = 'spec_ready' | 'build_ready' | 'complete';

export const PAGE_BUILD_SUMMARY: OperatorSummary = {
  spec: {
    title: 'Templated Content Fix — /emergency-electrician-langley/',
    greeting:
      "Hey Kyle, the Langley emergency page is 80% the same text as Hope's, city name swapped. Here's the new page section by section, from what a Langley searcher needs, aimed at \"emergency electrician langley\". It's written for a homeowner with the power out tonight, to get them to call. Change the buyer, the keyword or any section before you approve; each section is then built by its own agent, then the page faces a cold-buyer test and the SEO review.",
    sources: [{ id: 'screen', kind: 'other', label: 'Cockpit · Templated content · 80% shared with /emergency-electrician-hope/' }],
    requests: [
      { id: 'R1', source: 'screen', kind: 'build', text: 'Make the Langley emergency page its own: 80% the same as Hope today, under 20% after.' },
      { id: 'R2', source: 'screen', kind: 'build', text: 'Title and meta that answer "emergency electrician langley", once each.' },
    ],
    actions: [
      { request: 'R1', text: 'Replace the body with the six sections below, written from Langley searches and BDT’s own Langley jobs. Keep the URL, schema type and contact block.' },
      { request: 'R2', text: 'Title: "Emergency Electrician in Langley, BC | BDT Electric" (52). Meta rewritten around after-hours call-outs (148).' },
    ],
    repo: { name: 'eczzz/bdt-electric', note: 'src/pages/emergency-electrician-langley.astro only.' },
    db: { prod: null, preview: null, note: 'No database.' },
    need_to_know: [
      'Overlap is measured the way Cockpit measures it: 8-word runs, city names masked, against every sibling city page.',
      'Hope, Mission and Abbotsford share the same template; they are separate Templated Content Fix jobs.',
    ],
    decisions: [
      {
        id: 'D1',
        question: 'Keep the shared "Why BDT" block at the bottom?',
        options: ['Keep, shortened to two lines', 'Drop it'],
        recommendation: 'Keep, shortened to two lines',
        why: 'It is the one block every page should carry; short, it adds 3% overlap instead of 14%.',
      },
    ],
    approach: ['Sections from the Langley intent, not the template', 'Each section built in turn by its own agent', 'Generated images in the hero and the panels section', 'Re-measure overlap and density on the built page'],
    parked: ['Hope, Mission and Abbotsford pages: same template, separate jobs.'],
    buyer: {
      who: 'A Langley homeowner with no power, a burning smell or a breaker that won’t reset, often after dark, phone in hand.',
      wants: 'A licensed electrician at the door tonight, and to know what it will cost before anyone drives.',
      knows: 'Nothing about BDT. They have three electricians’ numbers from Google and will call whoever answers first and sounds sure.',
      objections: ['Will anyone actually come tonight?', 'Am I about to get gouged on an after-hours call?', 'Is this even an emergency, or can it wait?'],
      promise: 'A licensed electrician at most Langley homes in about 45 minutes, with the call-out fee said up front.',
      action: 'Call now',
    },
    keyword: {
      primary: 'emergency electrician langley',
      secondary: ['24 hour electrician langley', 'electrician langley'],
      volume: 210,
      source: 'search_console',
      why: 'The page’s top Search Console query (44 impressions at position 24) and 210 searches a month in Ahrefs; the URL and H1 already carry it.',
    },
    sections: [
      {
        id: 's1',
        level: 1,
        heading: 'Emergency Electrician in Langley, BC',
        purpose: 'The hero: answer the search in the first screen.',
        message: 'A licensed electrician reaches most of Langley in about 45 minutes, day or night, and the caller hears the call-out fee before anyone drives. One obvious way to call.',
        image: { idea: 'An electrician’s headlamp lighting an open panel in a dark Langley basement: urgent, calm, competent. No faces, no text.' },
      },
      {
        id: 's2',
        level: 2,
        heading: 'What counts as an electrical emergency',
        purpose: 'Help the caller decide: call now, or book for tomorrow.',
        message: 'Two clear lists side by side. Burning smells, scorched outlets, sparking and a hot panel mean call now; a dead outlet or a breaker that trips once and holds can wait for a daytime visit.',
      },
      {
        id: 's3',
        level: 2,
        heading: 'Older Langley homes and 100-amp panels',
        purpose: 'The local substance: the housing stock behind Langley’s after-hours calls.',
        message: 'Many pre-1990s Langley homes still run 100-amp panels that trip once a heat pump, EV charger or hot tub is added, usually on the coldest night. BDT makes it safe that night and quotes the upgrade separately.',
        image: { idea: 'A 1980s Fraser Valley split-level at dusk with frost on the lawn and one window dark.' },
      },
      {
        id: 's4',
        level: 2,
        heading: 'What happens when you call',
        purpose: 'Remove the unknowns: price, timing, what the electrician does first.',
        signature: true,
        message: 'Four steps, in order: we answer 24/7 and judge urgency, you hear the fee first, the electrician makes it safe and explains what failed, and anything that can wait is quoted for a daytime visit.',
        source: 'BDT’s call-out terms, from their booking page (keep as written): "Emergency call-out $189 within Langley, includes the first 30 minutes on site. After 30 minutes, $42 per quarter hour."',
      },
      {
        id: 's5',
        level: 2,
        heading: 'Areas we reach in Langley',
        purpose: 'Local proof of coverage without a wall of city names.',
        message: 'The Langley neighbourhoods they actually reach, shown as a place rather than a list: Willoughby, Walnut Grove, Murrayville, Brookswood, Aldergrove and Langley City.',
      },
      {
        id: 's6',
        level: 2,
        heading: 'Why BDT',
        purpose: 'The one shared block, kept to two lines (D1).',
        message: 'Licensed, insured and local to the Fraser Valley. One call, one electrician, no subcontracting.',
      },
    ],
  },
  build: {
    pr_url: 'https://github.com/eczzz/bdt-electric/pull/99',
    preview_url: 'https://deploy-preview-99--bdt-electric.netlify.app/emergency-electrician-langley/',
    deviations: [],
    buyer_test: {
      passed: true,
      what_it_is: 'An electrician in Langley who comes out tonight for emergencies.',
      for_whom: 'Me, with the power out at home right now.',
      next_step: 'Tap the call button; the fee is right there.',
      would_leave: ['If the number went to voicemail.'],
      rounds: 2,
    },
    seo_review: {
      url: 'https://deploy-preview-99--bdt-electric.netlify.app/emergency-electrician-langley/',
      liveUrl: 'https://bdtelectric.com/emergency-electrician-langley/',
      primary: 'emergency electrician langley',
      secondary: ['24 hour electrician langley', 'electrician langley'],
      summary: { pass: 27, warn: 2, fail: 0 },
      accepted: ['Meta description 164 chars: Google trims at ~160; the cut lands after "24/7", which still reads.', 'LCP 2.9 s on mobile: the hero webp is 118 KB; the rest is the theme’s font load, same on every page.'],
      checks: [
        { group: 'keyword', status: 'pass', label: '"emergency electrician langley" density 0.8%', detail: '6 uses in 2,214 body words; bar 0.5–1%' },
        { group: 'keyword', status: 'pass', label: 'Target in the title' },
        { group: 'keyword', status: 'pass', label: 'Target words in the H1' },
        { group: 'keyword', status: 'pass', label: 'Target in the first 100 words' },
        { group: 'keyword', status: 'pass', label: 'Target in the URL slug' },
        { group: 'keyword', status: 'pass', label: 'Secondary "24 hour electrician langley"', detail: '2 uses, 0.36%' },
        { group: 'keyword', status: 'pass', label: 'Secondary "electrician langley"', detail: '4 uses, 0.36%' },
        { group: 'head', status: 'pass', label: 'Title, 52 chars', detail: 'Emergency Electrician in Langley, BC | BDT Electric' },
        { group: 'head', status: 'warn', label: 'Meta description, 164 chars' },
        { group: 'head', status: 'pass', label: 'Canonical', detail: 'https://bdtelectric.com/emergency-electrician-langley/' },
        { group: 'head', status: 'pass', label: 'Indexable (no noindex meta)' },
        { group: 'head', status: 'pass', label: 'Open Graph title, description, image' },
        { group: 'headings', status: 'pass', label: '1 H1', detail: 'Emergency Electrician in Langley, BC' },
        { group: 'headings', status: 'pass', label: 'No skipped heading levels' },
        { group: 'images', status: 'pass', label: '3 images' },
        { group: 'images', status: 'pass', label: 'Every image has an alt attribute' },
        { group: 'images', status: 'pass', label: 'Descriptive, lowercase-hyphenated file names', detail: 'langley-panel-headlamp.webp, langley-split-level-frost.webp, bdt-logo.svg' },
        { group: 'images', status: 'pass', label: 'Modern formats (webp, avif, svg)' },
        { group: 'images', status: 'pass', label: 'width and height set (no layout shift)' },
        { group: 'schema', status: 'pass', label: 'Schema types: Electrician, PostalAddress, Service, BreadcrumbList' },
        { group: 'schema', status: 'pass', label: 'Every schema claim is on the page or in the facts', detail: 'phone, street address and postcode match the footer and the GBP listing' },
        { group: 'schema', status: 'pass', label: 'Schema URLs point at the live site' },
        { group: 'links', status: 'pass', label: '5 internal links in the body' },
        { group: 'links', status: 'pass', label: '24 internal links resolve' },
        { group: 'lighthouse', status: 'pass', label: 'Performance 91 (mobile)' },
        { group: 'lighthouse', status: 'pass', label: 'Accessibility 96' },
        { group: 'lighthouse', status: 'pass', label: 'Lighthouse SEO 100' },
        { group: 'lighthouse', status: 'warn', label: 'LCP 2.9 s' },
        { group: 'lighthouse', status: 'pass', label: 'CLS 0.002' },
      ],
    },
    checks: [
      {
        request: 'R1',
        before: '80% of the page also on Hope; 9 sentences shared word for word.',
        after: '6% against Hope, 4% against Mission, no shared sentence. Density "emergency electrician langley" 0.8%.',
        how_to_check: 'Open the preview, read sections 03 and 05: the Langley-only material.',
      },
      {
        request: 'R2',
        before: 'Title "Emergency Electrician Langley | BDT Electric | Emergency Electrician".',
        after: 'Title 52 characters, phrase once; meta 148 characters.',
        how_to_check: 'View source on the preview, look at <title> and the meta description.',
      },
    ],
  },
  approval: {
    live_url: 'https://bdtelectric.com/emergency-electrician-langley/',
    client_note:
      '**Your Langley emergency page is rewritten.** It was 80% the same as the Hope page; now it talks about Langley: older 100-amp panels, the neighbourhoods we reach, and what happens when someone calls. Same address, same phone number.',
    confirmed: [
      { request: 'R1', status: 'live', note: 'Live page measures 6% against Hope.' },
      { request: 'R2', status: 'live' },
    ],
    follow_ups: ['Request indexing in Search Console.'],
  },
};

const BASE: AiJob = {
  id: 'sample-page-build',
  task_id: 'sample-task',
  client_id: null,
  card_id: null,
  status: 'build_ready',
  kind: 'fulfillment',
  task_type: 'page_build',
  title: 'Templated Content Fix — BDT Electric /emergency-electrician-langley/',
  spec_body: '',
  report_body: '',
  approval_body: '',
  revision_note: '',
  handoff_notes: [],
  queue_note: '',
  brief: {},
  auto_approve: false,
  version: 3,
  claimed_by: null,
  claimed_at: null,
  created_by: null,
  created_at: '2026-10-09T08:00:00.000-06:00',
  updated_at: '2026-10-09T09:00:00.000-06:00',
  queue_position: 1,
  lock_reason: '',
  blocked_by_job_id: null,
  db_ref: null,
  parked_from: null,
  migration_version: null,
  model: null,
  operator_summary: null,
} as AiJob;

/** The sample at one of three stages. */
export function pageBuildJobAt(stage: GalleryStage): AiJob {
  const status = stage === 'spec_ready' ? 'spec_ready' : stage === 'build_ready' ? 'build_ready' : 'complete';
  return { ...BASE, status, version: stage === 'spec_ready' ? 2 : stage === 'build_ready' ? 3 : 4 } as AiJob;
}
